import React from 'react';
import { useNavigate } from 'react-router-dom';
import { equipmentDealAPI } from '../../api/api';
import { useApi } from '../../api/useApi';
import useIsMobile from '../../hooks/useIsMobile';
import FarmLifecycleLoader from '../common/FarmLifecycleLoader';

const fallbackImage = 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=800';

const getPrimaryImage = (deal) => {
  const images = Array.isArray(deal?.images) ? deal.images.filter(Boolean) : [];
  return images[0] || fallbackImage;
};

const shortText = (value, max = 88) => {
  const str = String(value || '').trim();
  if (!str) return 'Reliable farm equipment deal from verified Sedyam partners.';
  if (str.length <= max) return str;
  return `${str.slice(0, max).trim()}...`;
};

export default function EquipmentDealsSection() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data, loading, error } = useApi(equipmentDealAPI.getPublic, null, { staleTime: 45000 });

  const deals = Array.isArray(data) ? data : [];
  const featured = deals.slice(0, 3);
  const remainingCount = Math.max(deals.length - featured.length, 0);

  if (loading) {
    return (
      <section id="equipment-deals" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#f4fdf8' }}>
        <FarmLifecycleLoader compact title="Loading equipment deals" subtitle="Preparing verified tools, rentals, and machinery offers..." />
      </section>
    );
  }

  if (error) {
    return (
      <section id="equipment-deals" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#f4fdf8', textAlign: 'center' }}>
        <p style={{ color: '#dc2626' }}>Unable to load equipment deals: {error}</p>
      </section>
    );
  }

  return (
    <section id="equipment-deals" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#f4fdf8' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span
            style={{
              background: '#d8f3dc',
              color: '#2d6a4f',
              padding: '6px 20px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Farmer Store
          </span>
          <h2 style={{ fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: '700', color: '#1a1a2e', marginTop: '16px' }}>
            Equipment Deals
          </h2>
          <p style={{ color: '#6b7280', marginTop: '10px', fontSize: '1.05rem' }}>
            Verified equipment listings with multi-image previews and demo videos.
          </p>
        </div>

        {deals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px', borderRadius: '20px', background: 'white', color: '#6b7280' }}>
            No equipment deals are visible yet.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 230 : 260}px, 1fr))`,
              gap: isMobile ? '14px' : '24px',
            }}
          >
            {featured.map((deal) => {
              const mainImage = getPrimaryImage(deal);

              return (
                <div
                  key={deal._id}
                  onClick={() => navigate(`/equipment/${deal._id}`)}
                  style={{
                    background: 'white',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ height: '190px', background: '#f3f4f6' }}>
                    <img
                      src={mainImage}
                      alt={deal.name}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = fallbackImage;
                      }}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>

                  <div style={{ padding: '16px' }}>
                    <h3 style={{ fontSize: '1.02rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>{deal.name}</h3>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>{shortText(deal.description)}</p>
                    <p style={{ fontSize: '14px', fontWeight: '700', color: '#2d6a4f' }}>
                      Rs.{Number(deal.pricePerDay || 0).toLocaleString()} / Unit
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                      <span style={{ fontSize: '12px', color: '#4b5563' }}>{Number(deal.availableUnits || 0)} units</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: deal.demoVideoUrl ? '#2563eb' : '#9ca3af' }}>
                        {deal.demoVideoUrl ? 'Demo video available' : 'Demo video not added'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {remainingCount > 0 && (
              <button
                onClick={() => navigate('/equipment-store')}
                style={{
                  minHeight: isMobile ? '300px' : '332px',
                  borderRadius: '20px',
                  border: '1px dashed #52b788',
                  background: 'linear-gradient(135deg, rgba(216,243,220,0.96), rgba(255,255,255,0.96))',
                  boxShadow: '0 12px 32px rgba(45,106,79,0.08)',
                  padding: isMobile ? '24px 18px' : '28px 22px',
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                  gap: '12px',
                  color: '#1b4332',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: '#2d6a4f',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: '800',
                  }}
                >
                  +{remainingCount}
                </div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', margin: 0 }}>More Equipment Deals</h3>
                <p style={{ margin: 0, textAlign: 'center', color: '#52796f', lineHeight: 1.6 }}>
                  Explore all verified equipment listings in the farmer store.
                </p>
                <span
                  style={{
                    marginTop: '6px',
                    background: '#2d6a4f',
                    color: '#ffffff',
                    padding: '10px 18px',
                    borderRadius: '999px',
                    fontWeight: '700',
                    fontSize: '14px',
                  }}
                >
                  View All Deals
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
